/**
 * The always-focused rapid task entry field. Natural-language dates AND
 * recurrence parse as you type ("standup every monday 9am") with the phrases
 * highlighted via a mirror overlay under a transparent input (no
 * contentEditable, no caret fights). Enter adds and keeps focus; Tab accepts
 * the parse without adding; Esc keeps the text literal.
 */
import { useRef, useState } from "react";
import { CalendarClock, Repeat, X } from "lucide-react";
import { describeRecurrence, dueLabel, parseTaskInput, type ParsedTaskInput, type Recurrence } from "@hydra-notes/shared";
import s from "./TaskQuickAdd.module.css";

export interface QuickAddFields {
  title: string;
  dueDate?: string;
  dueTime?: string;
  recurrence?: Recurrence;
}

interface Pinned {
  dueDate?: string;
  dueTime?: string;
  recurrence?: Recurrence;
}

/** Render the mirror with every matched range highlighted. */
function mirrorParts(value: string, matches: { start: number; end: number }[]): { text: string; mark: boolean }[] {
  const parts: { text: string; mark: boolean }[] = [];
  let at = 0;
  for (const m of matches) {
    if (m.start > at) parts.push({ text: value.slice(at, m.start), mark: false });
    parts.push({ text: value.slice(m.start, m.end), mark: true });
    at = m.end;
  }
  if (at < value.length) parts.push({ text: value.slice(at), mark: false });
  return parts;
}

export function TaskQuickAdd({
  onAdd,
  placeholder = "Add a task... try \"standup every monday 9am\"",
  disabled,
  autoFocus,
}: {
  onAdd: (fields: QuickAddFields) => void;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
}) {
  const [value, setValue] = useState("");
  const [pinned, setPinned] = useState<Pinned | null>(null);
  const [suppressedFor, setSuppressedFor] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const mirrorRef = useRef<HTMLDivElement>(null);

  const parse: ParsedTaskInput | null = pinned || suppressedFor === value ? null : parseTaskInput(value);
  const dueDate = pinned?.dueDate ?? parse?.dueDate;
  const dueTime = pinned?.dueTime ?? parse?.dueTime;
  const recurrence = pinned?.recurrence ?? parse?.recurrence;

  const submit = () => {
    const title = (parse ? parse.title : value).trim();
    if (!title) return;
    onAdd({
      title,
      ...(dueDate ? { dueDate } : {}),
      ...(dueTime ? { dueTime } : {}),
      ...(recurrence ? { recurrence } : {}),
    });
    setValue("");
    setPinned(null);
    setSuppressedFor(null);
    inputRef.current?.focus();
  };

  return (
    <div className={s.wrap} data-disabled={disabled || undefined}>
      <div className={s.field}>
        {/* mirror renders the same text underneath; only matches are visible */}
        <div ref={mirrorRef} className={s.mirror} aria-hidden>
          {parse ? (
            mirrorParts(value, parse.matches).map((p, i) =>
              p.mark ? (
                <mark key={i} className={s.match}>
                  {p.text}
                </mark>
              ) : (
                <span key={i}>{p.text}</span>
              ),
            )
          ) : (
            <span>{value}</span>
          )}
        </div>
        <input
          ref={inputRef}
          className={s.input}
          type="text"
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          enterKeyHint="done"
          onChange={(e) => {
            setValue(e.target.value);
            if (suppressedFor !== null && e.target.value !== suppressedFor) setSuppressedFor(null);
          }}
          onScroll={(e) => {
            if (mirrorRef.current) mirrorRef.current.scrollLeft = e.currentTarget.scrollLeft;
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submit();
            } else if (e.key === "Tab" && parse) {
              e.preventDefault();
              setPinned({
                ...(parse.dueDate ? { dueDate: parse.dueDate } : {}),
                ...(parse.dueTime ? { dueTime: parse.dueTime } : {}),
                ...(parse.recurrence ? { recurrence: parse.recurrence } : {}),
              });
              setValue(parse.title);
            } else if (e.key === "Escape" && parse) {
              e.stopPropagation();
              setSuppressedFor(value);
            }
          }}
        />
      </div>
      {dueDate ? (
        <span className={s.chip} data-pinned={pinned ? "" : undefined}>
          <CalendarClock size={12} />
          {dueLabel(dueDate, dueTime)}
          {pinned ? (
            <button
              type="button"
              className={s.chipClear}
              aria-label="Remove due date"
              onClick={() => setPinned((p) => (p ? { ...p, dueDate: undefined, dueTime: undefined } : p))}
            >
              <X size={11} />
            </button>
          ) : (
            <kbd className={s.chipKbd}>Tab</kbd>
          )}
        </span>
      ) : null}
      {recurrence ? (
        <span className={s.chip} data-pinned={pinned ? "" : undefined}>
          <Repeat size={12} />
          {describeRecurrence(recurrence)}
          {pinned ? (
            <button
              type="button"
              className={s.chipClear}
              aria-label="Remove recurrence"
              onClick={() => setPinned((p) => (p ? { ...p, recurrence: undefined } : p))}
            >
              <X size={11} />
            </button>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}
